import { rolesManipulator, useGetUserRoleRegistersIDs } from 'auth';
import classNames from 'classnames';
import { ReactNode, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { AdminRegisterModal } from '@/modules/admin-dashboard/components/modals/AdminRegisterModal.tsx';
import { hasIncompleteRegistrationFields } from '@/modules/issuer-dashboard/helpers/registrationFields.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { DropdownMenuItem, ModalProviderContext, TooltipNew } from 'ui';
import { SettingsIcon, WarningYellowIcon } from 'ui/icons';
import { GetUserRegistersResponse } from 'api';

export const useIssuerSidebarHeaderMenuOptions = (
  registers?: GetUserRegistersResponse,
): (DropdownMenuItem & { labelCurrent?: ReactNode })[] => {
  const [params, setParams] = useSearchParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { openModal } = useContext(ModalProviderContext);
  const currentRoleRegistersIDs = useGetUserRoleRegistersIDs(rolesManipulator.current_user_role);

  const handleOpenEditRegisterModal = useCallback(
    (redactionRegisterId: number) => {
      openModal(AdminRegisterModal, { redactionRegisterId, role: 'issuer' });
    },
    [openModal],
  );

  const handleOpenFinishRegistration = useCallback(
    (redactionRegisterId: number) => {
      openModal(AdminRegisterModal, {
        redactionRegisterId,
        role: 'issuer',
        isFinishRegistrationMode: true,
      });
    },
    [openModal],
  );

  return useMemo<(DropdownMenuItem & { labelCurrent?: ReactNode })[]>(
    () =>
      (registers?.data || [])
        .filter(({ register_id }) => currentRoleRegistersIDs.includes(register_id))
        .map<DropdownMenuItem & { labelCurrent?: ReactNode }>(({ register_obj, register_id }) => ({
          label: (
            <div className="flex gap-3 items-center">
              {register_obj?.icon_url && (
                <span className="bg-white border-white border">
                  <img
                    src={register_obj?.icon_url}
                    alt="icon"
                    className="w-6 h-6 max-w-6 max-h-6"
                  />
                </span>
              )}
              <span>{register_obj?.name || ''}</span>
              {hasIncompleteRegistrationFields(register_obj) && (
                <span
                  className="cursor-pointer"
                  onClick={e => {
                    e.stopPropagation();
                    handleOpenFinishRegistration(register_id);
                  }}
                >
                  <TooltipNew
                    text="Some registration fields are empty. Click to complete your organisation details."
                    place="right"
                    showDelay={200}
                    hideDelay={300}
                    classNameTooltip="text-xs text-center max-w-56"
                  >
                    <img src={WarningYellowIcon} alt="incomplete" className="w-4 h-4 min-w-4" />
                  </TooltipNew>
                </span>
              )}
            </div>
          ),
          labelCurrent: (
            <div className="flex gap-3 items-center pr-3">
              {register_obj?.icon_url && (
                <span className="bg-white border-white border">
                  <img
                    src={register_obj?.icon_url}
                    alt="icon"
                    className="w-6 h-6 max-w-6 max-h-6"
                  />
                </span>
              )}
              {pathname.includes(issuerDashboardPaths.static.portfolios) ? (
                <>
                  <TooltipNew
                    text={register_obj?.name || ''}
                    className={classNames('text-ellipsis overflow-hidden whitespace-nowrap', {
                      'max-w-[92px]': !!register_obj?.icon_url,
                      'max-w-[116px]': !register_obj?.icon_url,
                    })}
                  >
                    {register_obj?.name || ''}
                  </TooltipNew>
                  <span className="rounded-sm px-1.5 bg-[#333333] text-[#959595] text-xs leading-5 font-[Inter]">
                    {register_obj?.currency?.toUpperCase()}
                  </span>
                </>
              ) : (
                <span>{register_obj?.name || ''}</span>
              )}
              {hasIncompleteRegistrationFields(register_obj) && (
                <span
                  className="cursor-pointer"
                  onClick={e => {
                    e.stopPropagation();
                    handleOpenFinishRegistration(register_id);
                  }}
                >
                  <TooltipNew
                    text="Some registration fields are empty. Click to complete your organisation details."
                    place="right"
                    showDelay={200}
                    hideDelay={300}
                    classNameTooltip="text-xs text-center max-w-56"
                  >
                    <img src={WarningYellowIcon} alt="incomplete" className="w-4 h-4 min-w-4" />
                  </TooltipNew>
                </span>
              )}
            </div>
          ),
          onClick: () => {
            const nextParams = new URLSearchParams(params);
            if (nextParams.get(REGISTER_ID_SEARCH_PARAM_KEY) === String(register_id)) return;

            nextParams.set(REGISTER_ID_SEARCH_PARAM_KEY, String(register_id));
            if (
              pathname === issuerDashboardPaths.static.portfolios ||
              pathname.startsWith(`${issuerDashboardPaths.static.portfolios}/`)
            ) {
              nextParams.delete('tab-portfolio');
              navigate({
                pathname: issuerDashboardPaths.static.portfolios,
                search: nextParams.toString(),
              });
              return;
            }

            setParams(nextParams, { replace: false });
          },
          value: String(register_id),
          // icon: register_obj?.icon_url,
          hoverRightButton: (
            <span
              className="cursor-pointer hover:opacity-70"
              onClick={e => {
                e.preventDefault();
                handleOpenEditRegisterModal(register_id);
              }}
            >
              <img src={SettingsIcon} alt="Setting" className="w-6 h-6 min-w-6 min-h-6" />
            </span>
          ),
        })),
    [
      currentRoleRegistersIDs,
      handleOpenEditRegisterModal,
      handleOpenFinishRegistration,
      navigate,
      params,
      pathname,
      registers?.data,
      setParams,
    ],
  );
};
